"use client";

// Per-message actions (assistant turns): Copy and a "…" menu holding an
// optional host caption (e.g. the model name) and the markdown-source toggle,
// plus thumbs up/down feedback when the host injects
// the `feedback` source (absent → no thumbs). Same affordances as the Magnus
// web app's transcript, driven by the shared copy pipeline in ./copy.ts.
// Single source for npm + registry.
import { c as _c } from "react/compiler-runtime";
import type { MagnusUIMessage } from "@bg-bgi/magnus-sdk";
import { Button } from "@workspace/ui/components/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@workspace/ui/components/dropdown-menu";
import { CheckIcon, CodeIcon, CopyIcon, MoreHorizontalIcon, ThumbsDownIcon, ThumbsUpIcon } from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";
import { copyToClipboard, markdownForCopy, markdownToClipboardHtml } from "./copy";

/** Per-message feedback render props, threaded from the transcript's
 *  `feedback` source. Absent → no thumbs. */
export interface MessageFeedbackProps {
  /** Opens the feedback modal for this message. */
  onVote: (vote: "up" | "down") => void;
  /** The caller's recorded vote on this message, if any. */
  vote?: "up" | "down";
}

/** Copy + feedback + View-markdown row rendered under a finished assistant message. */
export function MessageActions(t0) {
  const $ = _c(22);
  const {
    markdownView: t1,
    menuLabel,
    message,
    feedback,
    onToggleMarkdown
  } = t0;
  const markdownView = t1 === undefined ? false : t1;
  const [copied, setCopied] = useState(false);
  let t2;
  if ($[0] !== message) {
    t2 = markdownForCopy(message);
    $[0] = message;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const markdown = t2;
  let t3;
  if ($[2] !== markdown || $[3] !== markdownView) {
    t3 = async () => {
      const ok = markdownView ? await copyToClipboard(markdown) : await copyToClipboard(markdown, markdownToClipboardHtml(markdown));
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    };
    $[2] = markdown;
    $[3] = markdownView;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleCopy = t3;
  if (!markdown) {
    return null;
  }
  const hasMenuLabel = menuLabel != null && menuLabel !== false;
  const t4 = copied ? "Copied" : "Copy message";
  let t5;
  if ($[5] !== copied) {
    t5 = copied ? <CheckIcon className="size-3.5" /> : <CopyIcon className="size-3.5" />;
    $[5] = copied;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== handleCopy || $[8] !== t4 || $[9] !== t5) {
    t6 = <Button aria-label={t4} className="size-7" onClick={handleCopy} size="icon" type="button" variant="ghost">{t5}</Button>;
    $[7] = handleCopy;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== feedback) {
    t7 = feedback && <><Button aria-label={feedback.vote === "up" ? "Thank you for your feedback" : "Good response"} className={feedback.vote === "up" ? "size-7 text-foreground" : "size-7"} onClick={() => feedback.vote === undefined && feedback.onVote("up")} size="icon" type="button" variant="ghost"><ThumbsUpIcon className={feedback.vote === "up" ? "size-3.5 text-success" : "size-3.5"} /></Button><Button aria-label={feedback.vote === "down" ? "Thank you for your feedback" : "Bad response"} className={feedback.vote === "down" ? "size-7 text-foreground" : "size-7"} onClick={() => feedback.vote === undefined && feedback.onVote("down")} size="icon" type="button" variant="ghost"><ThumbsDownIcon className={feedback.vote === "down" ? "size-3.5 text-destructive" : "size-3.5"} /></Button></>;
    $[11] = feedback;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== hasMenuLabel || $[14] !== markdownView || $[15] !== menuLabel || $[16] !== onToggleMarkdown) {
    t8 = (onToggleMarkdown || hasMenuLabel) && <DropdownMenu><DropdownMenuTrigger render={<Button aria-label="More options" className="size-7" size="icon" type="button" variant="ghost" />}><MoreHorizontalIcon className="size-3.5" /></DropdownMenuTrigger><DropdownMenuContent align="start" className="w-auto whitespace-nowrap" side="top">{hasMenuLabel && <DropdownMenuGroup><DropdownMenuLabel>{menuLabel}</DropdownMenuLabel></DropdownMenuGroup>}{hasMenuLabel && onToggleMarkdown && <DropdownMenuSeparator />}{onToggleMarkdown && <DropdownMenuItem onClick={onToggleMarkdown}><CodeIcon className="size-3.5" />{markdownView ? "View rendered" : "View markdown"}</DropdownMenuItem>}</DropdownMenuContent></DropdownMenu>;
    $[13] = hasMenuLabel;
    $[14] = markdownView;
    $[15] = menuLabel;
    $[16] = onToggleMarkdown;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = <div className="-ml-2 flex items-center gap-0.5 text-muted-foreground">{t6}{t7}{t8}</div>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}